import { useTranslation } from '@pancakeswap/localization'
import { ChainId } from '@pancakeswap/sdk'
import dayjs from 'dayjs'
import {
  AutoColumn,
  Box,
  Breadcrumbs,
  Card,
  Flex,
  Heading,
  LinkExternal,
  NextLinkFromReactRouter,
  Spinner,
  Text,
  useMatchBreakpoints,
  Button,
} from '@pancakeswap/uikit'
import Page from 'components/Layout/Page'
import { TabToggle, TabToggleGroup } from 'components/TabToggle'
import { CHAIN_QUERY_NAME } from 'config/chains'
// import { useActiveChainId } from 'hooks/useActiveChainId'
import useTheme from 'hooks/useTheme'
import { useEffect, useMemo, useState } from 'react'
import { multiChainId, multiChainScan } from 'state/info/constant'
import { useChainNameByQuery, useMultiChainPath, useStableSwapPath } from 'state/info/hooks'
import styled from 'styled-components'
import { getBlockExploreLink } from 'utils'
import { formatAmount } from 'utils/formatInfoNumbers'
import { CurrencyLogo, DoubleCurrencyLogo } from 'views/Info/components/CurrencyLogo'
import BarChart from '../components/BarChart/alt'
import LineChart from '../components/LineChart/alt'
import { GreyBadge } from '../components/Card'
import DensityChart from '../components/DensityChart'
import { LocalLoader } from '../components/Loader'
import Percent from '../components/Percent'
import { RowBetween, RowFixed } from '../components/Row'
import TransactionTable from '../components/TransactionsTable'
import { v3InfoPath } from '../constants'
import { usePoolChartData, usePoolData, usePoolTransactions } from '../hooks'
import { feeTierPercent } from '../utils'
import { unixToDate } from '../utils/date'
import { formatDollarAmount } from '../utils/numbers'
import { fmtToFixed } from 'utils/numberFormat'

const ContentLayout = styled.div`
  /* display: grid;
  grid-template-columns: 300px 1fr;
  grid-gap: 1em;

  @media screen and (max-width: 800px) {
    grid-template-columns: 1fr;
    grid-template-rows: 1fr 1fr;
  } */

  @media not screen and (max-width: 1280px) {
    display: grid;
    grid-template-columns: 300px 1fr;
    grid-gap: 1em;
  }
  @media screen and (max-width: 1280px) {
    /* display: block; */
    display: grid;
    /* grid-template-columns: 300px 1fr; */
    grid-gap: 32px;
    grid-template-columns: 1fr;
    /* grid-template-rows: 1fr 1fr; */
    /* .box + .box {
      margin-top: 32px;
    } */
  }
`

const TokenButton = styled(Flex)`
  padding: 8px 0px;
  margin-right: 16px;
  :hover {
    cursor: pointer;
    opacity: 0.6;
  }
`

enum ChartView {
  VOL,
  PRICE,
  DENSITY,
  FEES,
  TVL,
}

const PoolPage: React.FC<{ address: string }> = ({ address }) => {
  // const { chainId } = useActiveChainId()
  const { isXs, isSm } = useMatchBreakpoints()

  const now = dayjs()
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // theming

  const { theme } = useTheme()
  const backgroundColor = theme.colors.primary

  // token data
  const poolData = usePoolData(address)
  const chartData = usePoolChartData(address)
  const transactions = usePoolTransactions(address)

  const [view, setView] = useState(ChartView.VOL)
  const [latestValue, setLatestValue] = useState<number | undefined>()
  const [valueLabel, setValueLabel] = useState<string | undefined>()

  const hasSmallDifference = useMemo(() => {
    return poolData ? Math.abs(poolData.token1Price - poolData.token0Price) < 1 : false
  }, [poolData])

  const formattedTvlData = useMemo(() => {
    if (chartData) {
      return chartData.map((day) => {
        return {
          time: unixToDate(day.date),
          value: day.totalValueLockedUSD,
        }
      })
    }
    return []
  }, [chartData])

  const formattedVolumeData = useMemo(() => {
    if (chartData) {
      return chartData.map((day) => {
        return {
          time: unixToDate(day.date),
          value: day.volumeUSD,
        }
      })
    }
    return []
  }, [chartData])

  const formattedFeesUSD = useMemo(() => {
    if (chartData) {
      return chartData.map((day) => {
        return {
          time: unixToDate(day.date),
          value: day.feesUSD,
        }
      })
    }
    return []
  }, [chartData])

  const chainName = useChainNameByQuery()
  const chainPath = useMultiChainPath()
  const infoTypeParam = useStableSwapPath()
  const { t } = useTranslation()
  return (
    <Page>
      {poolData ? (
        <AutoColumn gap="32px">
          <Flex justifyContent="space-between" mb="16px" flexDirection={['column', 'column', 'row']}>
            <Breadcrumbs mb="32px">
              <NextLinkFromReactRouter to={`/${v3InfoPath}${chainPath}${infoTypeParam}`}>
                <Text color="primary">{t('Info')}</Text>
              </NextLinkFromReactRouter>
              {/* <NextLinkFromReactRouter to={`/${v3InfoPath}${chainPath}/pairs${infoTypeParam}`}>
                <Text color="primary">{t('Pairs')}</Text>
              </NextLinkFromReactRouter> */}
              <Flex>
                <Text mr="8px">
                  {`${poolData.token0.symbol} / ${poolData.token1.symbol}
                `}
                  <GreyBadge ml="4px" style={{ display: 'inline-block' }}>
                    {feeTierPercent(poolData.feeTier)}
                  </GreyBadge>
                </Text>
              </Flex>
            </Breadcrumbs>

            <Flex justifyContent={[null, null, 'flex-end']} mt={['8px', '8px', 0]}>
              <LinkExternal
                isBscScan={multiChainId[chainName] === ChainId.MANTLE}
                mr="8px"
                href={getBlockExploreLink(address, 'address', multiChainId[chainName])}
              >
                {t('View on %site%', { site: multiChainScan[chainName] })}
              </LinkExternal>
              {/* <SaveIcon fill={watchlistPools.includes(address)} onClick={() => addPoolToWatchlist(address)} /> */}
            </Flex>
          </Flex>
          <Flex flexDirection="column">
            <Flex alignItems="center" mb={['8px', null]}>
              <DoubleCurrencyLogo
                symbol0={poolData.token0.symbol}
                symbol1={poolData.token1.symbol}
                address0={poolData.token0.address}
                address1={poolData.token1.address}
                size={32}
                chainName={chainName}
              />
              <Text ml="38px" bold fontSize={isXs || isSm ? '24px' : '24px'} id="info-pool-pair-title">
                {`${poolData.token0.symbol} / ${poolData.token1.symbol}`}{' '}
                <GreyBadge
                  ml="4px"
                  style={{
                    padding: '6px 8px',
                    display: 'inline-block',
                    fontSize: '16px',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    lineHeight: '100%',
                  }}
                >
                  {feeTierPercent(poolData.feeTier)}
                </GreyBadge>
              </Text>
            </Flex>
            <Flex mt={16} justifyContent="space-between" flexDirection={['column', 'column', 'column', 'row']}>
              <Flex style={{ gap: '8px' }} flexDirection={['column', 'column', 'row']} mb={['8px', '8px', null]}>
                <NextLinkFromReactRouter
                  style={{ padding: '0 8px', borderRadius: '8px', background: 'rgba(255, 255, 255, 0.16)' }}
                  to={`/${v3InfoPath}${chainPath}/tokens/${poolData.token0.address}${infoTypeParam}`}
                >
                  <TokenButton style={{ marginRight: '0' }}>
                    <CurrencyLogo
                      symbol={poolData.token0.symbol}
                      address={poolData.token0.address}
                      size="24px"
                      chainName={chainName}
                    />
                    <Text
                      lineHeight="normal"
                      fontSize="16px"
                      ml="4px"
                      style={{ whiteSpace: 'nowrap' }}
                      width="fit-content"
                    >
                      {`1 ${poolData.token0.symbol} =  ${formatAmount(poolData.token1Price, {
                        notation: 'standard',
                        displayThreshold: 0.001,
                        tokenPrecision: hasSmallDifference ? 'enhanced' : 'normal',
                      })} ${poolData.token1.symbol}`}
                    </Text>
                  </TokenButton>
                </NextLinkFromReactRouter>
                <NextLinkFromReactRouter
                  style={{ padding: '0 8px', borderRadius: '8px', background: 'rgba(255, 255, 255, 0.16)' }}
                  to={`/${v3InfoPath}${chainPath}/tokens/${poolData.token1.address}${infoTypeParam}`}
                >
                  <TokenButton style={{ marginRight: '0' }} ml="8px">
                    <CurrencyLogo
                      symbol={poolData.token1.symbol}
                      address={poolData.token1.address}
                      size="24px"
                      chainName={chainName}
                    />
                    <Text
                      lineHeight="normal"
                      fontSize="16px"
                      ml="4px"
                      style={{ whiteSpace: 'nowrap' }}
                      width="fit-content"
                    >
                      {`1 ${poolData.token1.symbol} =  ${formatAmount(poolData.token0Price, {
                        notation: 'standard',
                        displayThreshold: 0.001,
                        tokenPrecision: hasSmallDifference ? 'enhanced' : 'normal',
                      })} ${poolData.token0.symbol}`}
                    </Text>
                  </TokenButton>
                </NextLinkFromReactRouter>
              </Flex>
              <Flex>
                <NextLinkFromReactRouter
                  to={`/add/${poolData.token0.address}/${poolData.token1.address}?chain=${
                    CHAIN_QUERY_NAME[multiChainId[chainName]]
                  }`}
                >
                  <Button mr="8px" variant="secondary">
                    {t('Add Liquidity')}
                  </Button>
                </NextLinkFromReactRouter>
                <NextLinkFromReactRouter
                  to={`/swap?inputCurrency=${poolData.token0.address}&outputCurrency=${poolData.token1.address}&chain=${
                    CHAIN_QUERY_NAME[multiChainId[chainName]]
                  }`}
                >
                  <Button>{t('Trade')}</Button>
                </NextLinkFromReactRouter>
              </Flex>
            </Flex>
          </Flex>
          <ContentLayout>
            <Card className="box">
              <Box p="12px">
                {/* <Card mb="24px"> */}
                <AutoColumn
                  style={{
                    background: 'rgba(255, 255, 255, 0.04)',
                    borderRadius: '20px',
                  }}
                  padding="16px"
                  gap="md"
                  mb="24px"
                >
                  <Text color="rgba(255, 255, 255, 0.45)" fontSize="12px" fontWeight={700}>
                    {t('Total Tokens Locked')}
                  </Text>
                  <RowBetween>
                    <RowFixed>
                      <CurrencyLogo
                        symbol={poolData.token0.symbol}
                        address={poolData.token0.address}
                        size="20px"
                        chainName={chainName}
                      />
                      <Text fontSize="14px" ml="8px">
                        {poolData.token0.symbol}
                      </Text>
                    </RowFixed>
                    <Text fontSize="14px">{formatAmount(poolData.tvlToken0)}</Text>
                  </RowBetween>
                  <RowBetween>
                    <RowFixed>
                      <CurrencyLogo
                        symbol={poolData.token1.symbol}
                        address={poolData.token1.address}
                        size="20px"
                        chainName={chainName}
                      />
                      <Text fontSize="14px" ml="8px">
                        {poolData.token1.symbol}
                      </Text>
                    </RowFixed>
                    <Text fontSize="14px">{formatAmount(poolData.tvlToken1)}</Text>
                  </RowBetween>
                </AutoColumn>
                {/* </Card> */}
                <Box mb="20px">
                  <Text lineHeight={1.3} fontWeight={400} fontSize={16}>
                    {t('TVL')}
                  </Text>
                  <Flex mt="8px" alignItems="center" style={{ gap: '8px' }}>
                    <Text lineHeight={1} fontWeight={700} fontSize={20}>
                      {formatDollarAmount(poolData.tvlUSD)}
                    </Text>
                    <Percent value={poolData.tvlUSDChange} />
                  </Flex>
                </Box>
                <Box mb="20px">
                  <Text lineHeight={1.3} fontWeight={400} fontSize={16}>
                    {t('Volume 24H')}
                  </Text>
                  <Flex mt="8px" alignItems="center" style={{ gap: '8px' }}>
                    <Text lineHeight={1} fontWeight={700} fontSize={20}>
                      {formatDollarAmount(poolData.volumeUSD)}
                    </Text>
                    <Percent value={poolData.volumeUSDChange} />
                  </Flex>
                </Box>
                <Box mb="20px">
                  <Text lineHeight={1.3} fontWeight={400} fontSize={16}>
                    {t('Fees 24H')}
                  </Text>
                  <Text mt="8px" lineHeight={1} fontWeight={700} fontSize={20}>
                    {formatDollarAmount(poolData.feeUSD)}
                  </Text>
                </Box>

                <Box mb="20px">
                  <Text lineHeight={1.3} fontWeight={400} fontSize={16}>
                    {t('APR(7D)')}
                  </Text>
                  <Text mt="8px" lineHeight={1} fontWeight={700} fontSize={20}>
                    {fmtToFixed(poolData.apr,2)}%
                  </Text>
                </Box>

              </Box>
            </Card>
            <Card className="box">
              <TabToggleGroup>
                <TabToggle isActive={view === ChartView.VOL} onClick={() => setView(ChartView.VOL)}>
                  <Text>{t('Volume')}</Text>
                </TabToggle>
                <TabToggle isActive={view === ChartView.DENSITY} onClick={() => setView(ChartView.DENSITY)}>
                  <Text>{t('Liquidity')}</Text>
                </TabToggle>
                <TabToggle isActive={view === ChartView.FEES} onClick={() => setView(ChartView.FEES)}>
                  <Text>{t('Fees')}</Text>
                </TabToggle>
                <TabToggle isActive={view === ChartView.TVL} onClick={() => setView(ChartView.TVL)}>
                  <Text>{t('TVL')}</Text>
                </TabToggle>
              </TabToggleGroup>
              <Flex flexDirection="column" px="24px" pt="24px">
                {latestValue
                  ? formatDollarAmount(latestValue)
                  : view === ChartView.VOL
                  ? formatDollarAmount(formattedVolumeData[formattedVolumeData.length - 1]?.value)
                  : view === ChartView.DENSITY
                  ? ''
                  : view === ChartView.TVL
                  ? formatDollarAmount(formattedTvlData[formattedTvlData.length - 1]?.value)
                  : formatDollarAmount(formattedFeesUSD[formattedFeesUSD.length - 1]?.value)}
                <Text small color="headerColor">
                  {view !== ChartView.DENSITY && `${valueLabel ?? now.format('MMM D, YYYY')} (UTC)`}
                </Text>
              </Flex>
              <Box height="380px">
                {view === ChartView.VOL ? (
                  <BarChart
                    data={formattedVolumeData}
                    color={backgroundColor}
                    minHeight={340}
                    setValue={setLatestValue}
                    setLabel={setValueLabel}
                    value={latestValue}
                    label={valueLabel}
                  />
                ) : view === ChartView.FEES ? (
                  <BarChart
                    data={formattedFeesUSD}
                    color={backgroundColor}
                    minHeight={340}
                    setValue={setLatestValue}
                    setLabel={setValueLabel}
                    value={latestValue}
                    label={valueLabel}
                  />
                ) : view === ChartView.TVL ? (
                  <LineChart
                    data={formattedTvlData}
                    minHeight={340}
                    setValue={setLatestValue}
                    setLabel={setValueLabel}
                    value={latestValue}
                    label={valueLabel}
                  />
                ) : (
                  <DensityChart address={address} />
                )}
              </Box>
            </Card>
          </ContentLayout>
          {/* <Heading>{t('Transactions')}</Heading> */}
          {transactions ? <TransactionTable transactions={transactions} /> : <LocalLoader fill={false} />}
        </AutoColumn>
      ) : (
        <Flex mt="80px" justifyContent="center">
          <Spinner />
        </Flex>
      )}
    </Page>
  )
}

export default PoolPage
